import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Loader2 } from 'lucide-react'
import type {
  Fuente,
  FuenteCreateInput,
  FuenteUpdateInput,
} from '@/modules/bd-unificada/types/fuente'
import { TIPOS_FUENTE } from '@/modules/bd-unificada/types/fuente'

const schema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  tipo: z.string().min(1, 'El tipo es requerido'),
  prioridad: z.coerce.number().int().min(1, 'Mínimo 1').max(10, 'Máximo 10'),
})

type FormData = z.infer<typeof schema>

interface FuenteFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: FuenteCreateInput | FuenteUpdateInput) => Promise<any> | void
  isSubmitting: boolean
  fuente?: Fuente | null
  mode: 'create' | 'edit'
}

export function FuenteFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  fuente,
  mode,
}: FuenteFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { nombre: '', tipo: '', prioridad: 5 },
  })

  useEffect(() => {
    if (mode === 'edit' && fuente) {
      reset({ nombre: fuente.nombre, tipo: String(fuente.tipo), prioridad: fuente.prioridad })
    } else if (mode === 'create') {
      reset({ nombre: '', tipo: '', prioridad: 5 })
    }
  }, [mode, fuente, reset, open])

  const tipo = watch('tipo')
  const prioridad = watch('prioridad')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Nueva fuente' : 'Editar fuente'}</DialogTitle>
          <DialogDescription>
            La prioridad decide la deduplicación: ≥ 7 actualiza automáticamente, &lt; 7 envía los
            duplicados a la cola de revisión.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="nombre">Nombre</Label>
            <Input id="nombre" {...register('nombre')} />
            {errors.nombre && <p className="text-xs text-destructive">{errors.nombre.message}</p>}
          </div>

          <div className="space-y-1">
            <Label>Tipo</Label>
            <Select value={tipo || ''} onValueChange={(v) => setValue('tipo', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un tipo" />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_FUENTE.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.tipo && <p className="text-xs text-destructive">{errors.tipo.message}</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="prioridad">Prioridad (1–10)</Label>
            <Input id="prioridad" type="number" min={1} max={10} {...register('prioridad')} />
            <p className="text-xs text-muted-foreground">
              {Number(prioridad) >= 7
                ? 'Los duplicados se actualizarán automáticamente.'
                : 'Los duplicados irán a la cola de revisión manual.'}
            </p>
            {errors.prioridad && (
              <p className="text-xs text-destructive">{errors.prioridad.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-blue-800 hover:bg-blue-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Crear fuente' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
